/**
 * Этап 4 / KS-07 — the rich-text editor (TipTap/ProseMirror).
 *
 * Replaces the `<textarea>` + marker-wrapping toolbar that inserted `**` / `*` /
 * `__` into the text. That approach could not express what the client asked for —
 * colour, and an image in a signature — because Markdown has no grammar for either
 * (ADR 2026-07-29 «KS-07»).
 *
 * Extensions: StarterKit (paragraphs, headings, lists, bold/italic/strike/code,
 * blockquote, hr, hard-break AND — in v3 — underline + link, so those must NOT be
 * added separately or ProseMirror registers duplicates) plus TextStyle + Color,
 * which StarterKit does not carry.
 *
 * The editor emits HTML restricted to the shared allow-list
 * (`@eer/shared/richText`), and it is not the security boundary: the server
 * sanitizes on ingest and again before an email leaves
 * (`api/services/htmlSanitize.ts`).
 */
import { useEffect, useRef, type JSX } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Image } from '@tiptap/extension-image';
import {
  editorInitialContent,
  normalizeEditorOutput,
  isSameRichText,
  normalizeLinkHref,
  RICH_TEXT_COLORS,
  RICH_TEXT_COLOR_CLEAR_LABEL,
  RICH_TEXT_TOOLBAR,
  type RichTextCommandId,
} from './richTextEditorModel.js';
import {
  INLINE_IMAGE_MIME_TYPES,
  INLINE_IMAGE_MAX_BASE64_BYTES,
  isInlineImageDataUri,
  isInlineImageWithinLimit,
} from '@eer/shared';

export interface RichTextEditorProps {
  /** RAW stored value — Markdown or HTML (converted on load). */
  readonly value: string;
  /** Fired with the HTML the editor holds; '' for a visually-empty document. */
  readonly onChange: (html: string) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly testId?: string;
  readonly ariaLabel?: string;
  /** Min height of the writing area (Tailwind class), e.g. `min-h-40`. */
  readonly minHeightClass?: string;
  /**
   * Enables the "insert image" button. Absent by default: an image only makes sense
   * where the surface actually carries one (the recruiter signature — KS-06 ч.2), and
   * offering it on every email body would invite pasting screenshots into a message
   * whose size budget is already spent on the attached document.
   *
   * Called with a REJECTION reason when the picked file cannot be used, so the host
   * can show it through its own toast instead of this component inventing one.
   */
  readonly onInsertImage?: (rejection: string | null) => void;
}

const BTN_BASE =
  'inline-flex h-7 min-w-7 items-center justify-center rounded px-1.5 text-xs font-medium transition-colors disabled:opacity-40';
const BTN_IDLE = 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)]';
const BTN_ACTIVE = 'bg-[var(--color-primary)] text-white';

/** Is the mark/node behind this button active at the caret? */
function isActive(editor: Editor, id: RichTextCommandId): boolean {
  switch (id) {
    case 'bold':
      return editor.isActive('bold');
    case 'italic':
      return editor.isActive('italic');
    case 'underline':
      return editor.isActive('underline');
    case 'strike':
      return editor.isActive('strike');
    case 'h1':
      return editor.isActive('heading', { level: 1 });
    case 'h2':
      return editor.isActive('heading', { level: 2 });
    case 'h3':
      return editor.isActive('heading', { level: 3 });
    case 'bulletList':
      return editor.isActive('bulletList');
    case 'orderedList':
      return editor.isActive('orderedList');
    case 'blockquote':
      return editor.isActive('blockquote');
    case 'link':
      return editor.isActive('link');
    default:
      return false;
  }
}

/**
 * Run a toolbar command.
 *
 * `link` is the only one that needs input. It uses `window.prompt` deliberately: a
 * bespoke link popover is a design decision, and the Stage Gate for this work says
 * implement directly without a prototype — so the plain prompt goes in now rather
 * than inventing UI. An invalid scheme is reported instead of being silently dropped
 * by the sanitizer later (`normalizeLinkHref`).
 */
function runCommand(editor: Editor, id: RichTextCommandId, onLinkError: () => void): void {
  const chain = editor.chain().focus();
  switch (id) {
    case 'bold':
      chain.toggleBold().run();
      return;
    case 'italic':
      chain.toggleItalic().run();
      return;
    case 'underline':
      chain.toggleUnderline().run();
      return;
    case 'strike':
      chain.toggleStrike().run();
      return;
    case 'h1':
      chain.toggleHeading({ level: 1 }).run();
      return;
    case 'h2':
      chain.toggleHeading({ level: 2 }).run();
      return;
    case 'h3':
      chain.toggleHeading({ level: 3 }).run();
      return;
    case 'bulletList':
      chain.toggleBulletList().run();
      return;
    case 'orderedList':
      chain.toggleOrderedList().run();
      return;
    case 'blockquote':
      chain.toggleBlockquote().run();
      return;
    case 'unlink':
      chain.unsetLink().run();
      return;
    case 'clear':
      chain.unsetAllMarks().clearNodes().run();
      return;
    case 'link': {
      const previous = (editor.getAttributes('link').href as string | undefined) ?? '';
      const typed = window.prompt('Link address', previous);
      if (typed === null) return;
      if (typed.trim() === '') {
        chain.unsetLink().run();
        return;
      }
      const href = normalizeLinkHref(typed);
      if (!href) {
        onLinkError();
        return;
      }
      chain.extendMarkRange('link').setLink({ href }).run();
      return;
    }
  }
}

/**
 * Read a picked file into the document as a `data:` image (KS-06 ч.2).
 *
 * Both guards report through `notify` rather than failing quietly, because both are
 * mistakes a person actually makes: picking a `.svg` (which is a scriptable document,
 * not a bitmap) and picking a phone photo (megabytes, re-sent with every email).
 */
async function insertImageFile(
  editor: Editor,
  file: File,
  notify: (rejection: string | null) => void,
): Promise<void> {
  if (!INLINE_IMAGE_MIME_TYPES.includes(file.type)) {
    notify(`Only PNG, JPEG, GIF and WebP are supported (got ${file.type || 'an unknown type'})`);
    return;
  }
  const dataUri = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  }).catch(() => '');

  if (!isInlineImageDataUri(dataUri)) {
    notify('Could not read the file as an image');
    return;
  }
  if (!isInlineImageWithinLimit(dataUri)) {
    const limitKb = Math.round(INLINE_IMAGE_MAX_BASE64_BYTES / 1024);
    notify(`Image too large: the signature travels with every email, limit ~${limitKb} KB`);
    return;
  }
  editor.chain().focus().setImage({ src: dataUri, alt: file.name }).run();
  notify(null);
}

/** Rich-text editor with a formatting toolbar. Controlled via `value`/`onChange`. */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled = false,
  testId,
  ariaLabel,
  minHeightClass = 'min-h-32',
  onInsertImage,
}: RichTextEditorProps): JSX.Element {
  const fileRef = useRef<HTMLInputElement | null>(null);
  /**
   * The last value this editor itself emitted. Without it, the parent's re-render
   * pushes that same value straight back in (see the sync effect below) and
   * `setContent` resets the caret to the start of the document — the classic
   * "typing jumps to the beginning" bug in a controlled rich-text field.
   */
  const emitted = useRef<string>(normalizeEditorOutput(editorInitialContent(value)));

  const editor = useEditor({
    extensions: [
      // `link` ships inside StarterKit v3; configured here rather than added again.
      StarterKit.configure({
        link: {
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { rel: 'noopener noreferrer' },
        },
      }),
      TextStyle,
      Color,
      // StarterKit has no image node. `inline: false` keeps a logo as its own block
      // (an inline image inside a paragraph is what makes signatures render oddly in
      // Outlook), and `allowBase64` is required because that is how the image is
      // stored — see @eer/shared/inlineImages for why, and how it becomes a `cid:`
      // attachment on send.
      Image.configure({ inline: false, allowBase64: true }),
    ],
    content: editorInitialContent(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: `${minHeightClass} w-full px-3 py-2 text-sm leading-relaxed focus:outline-none`,
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
        // `<testId>-input` marks the CONTENTEDITABLE itself, not the frame around
        // it. `testId` lands on the outer <div> (toolbar + writing area), which is
        // not fillable — a test that types into it fails with "Element is not an
        // <input>, <textarea>, <select> or [contenteditable]". That is exactly how
        // the two Pre-Kickoff e2e specs broke when this editor replaced a textarea,
        // so the editable node gets its own handle rather than leaving every caller
        // to dig for `.ProseMirror`.
        ...(testId ? { 'data-testid': `${testId}-input` } : {}),
      },
    },
    onUpdate: ({ editor: e }) => {
      const html = normalizeEditorOutput(e.getHTML());
      emitted.current = html;
      onChange(html);
    },
  });

  // Adopt a value that changed OUTSIDE the editor (a regenerated document, a
  // different draft option, a form reset). Skipped when it matches what we just
  // emitted, which is what keeps the caret in place while typing.
  useEffect(() => {
    if (!editor) return;
    if (isSameRichText(value, emitted.current)) return;
    const next = editorInitialContent(value);
    emitted.current = normalizeEditorOutput(next);
    editor.commands.setContent(next, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return <div data-testid={testId} />;

  const currentColor = (editor.getAttributes('textStyle').color as string | undefined) ?? '';

  return (
    <div
      className="rounded-md border border-[var(--color-border)] bg-white focus-within:border-[var(--color-primary)]"
      data-testid={testId}
      // E03-F07-T08: callers wrap the editor in `<label>Body …</label>`. A click on a
      // contenteditable is NOT "interactive content" for label activation, so the
      // browser forwards it to the label's first labelable descendant — our first
      // toolbar <button> — focuses it and the editor loses the caret: the body looks
      // read-only while Subject (a plain <input>) works. Cancelling the click's
      // default action here stops that activation. The hidden file input is exempt:
      // its programmatic `.click()` bubbles through here and must open the picker.
      onClick={(e) => {
        if (e.target === fileRef.current) return;
        if (e.currentTarget.closest('label')) e.preventDefault();
      }}
    >
      <div
        className="flex flex-wrap items-center gap-0.5 border-b border-[var(--color-border)] px-1.5 py-1"
        role="toolbar"
        aria-label="Formatting"
        data-testid={testId ? `${testId}-toolbar` : undefined}
      >
        {RICH_TEXT_TOOLBAR.map((group, gi) => (
          <span key={group[0]!.id} className="flex items-center gap-0.5">
            {gi > 0 ? (
              <span className="mx-1 h-4 w-px bg-[var(--color-border)]" aria-hidden />
            ) : null}
            {group.map((item) => (
              <button
                key={item.id}
                type="button"
                title={item.label}
                aria-label={item.label}
                aria-pressed={isActive(editor, item.id)}
                disabled={disabled}
                data-testid={`rte-${item.id}`}
                className={`${BTN_BASE} ${isActive(editor, item.id) ? BTN_ACTIVE : BTN_IDLE}`}
                // onMouseDown, not onClick: a click would blur the editor first and
                // the selection the command has to act on would already be gone.
                onMouseDown={(e) => {
                  e.preventDefault();
                  runCommand(editor, item.id, () =>
                    window.alert('Only http://, https:// and mailto: links are allowed'),
                  );
                }}
              >
                {item.glyph}
              </button>
            ))}
          </span>
        ))}
        {onInsertImage ? (
          <>
            <span className="mx-1 h-4 w-px bg-[var(--color-border)]" aria-hidden />
            <button
              type="button"
              title="Insert image"
              aria-label="Insert image"
              disabled={disabled}
              data-testid="rte-image"
              className={`${BTN_BASE} ${BTN_IDLE}`}
              onMouseDown={(e) => {
                e.preventDefault();
                fileRef.current?.click();
              }}
            >
              🖼
            </button>
            <input
              ref={fileRef}
              type="file"
              accept={INLINE_IMAGE_MIME_TYPES.join(',')}
              className="hidden"
              data-testid="rte-image-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset first: picking the SAME file twice must fire onChange again.
                e.target.value = '';
                if (file) void insertImageFile(editor, file, onInsertImage);
              }}
            />
          </>
        ) : null}
        <span className="mx-1 h-4 w-px bg-[var(--color-border)]" aria-hidden />
        {/* Цвет — кружки, а не список названий (фидбэк Ксении 2026-07-31). Цвет
            выбирают глазами; список слов заставляет читать, и его ещё надо
            открыть, чтобы увидеть, что там вообще есть. Кружки лежат в самом
            тулбаре, поэтому выбор — один клик, а активный виден по обводке. */}
        <span
          role="group"
          aria-label="Text colour"
          data-testid="rte-color"
          className="flex items-center gap-0.5"
        >
          <button
            type="button"
            title={RICH_TEXT_COLOR_CLEAR_LABEL}
            aria-label={RICH_TEXT_COLOR_CLEAR_LABEL}
            aria-pressed={currentColor === ''}
            disabled={disabled}
            data-testid="rte-color-clear"
            className={`${BTN_BASE} ${currentColor === '' ? BTN_ACTIVE : BTN_IDLE}`}
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().unsetColor().run();
            }}
          >
            ⌫
          </button>
          {RICH_TEXT_COLORS.map((c) => (
            <button
              key={c.value}
              type="button"
              title={c.label}
              aria-label={c.label}
              aria-pressed={currentColor.toLowerCase() === c.value}
              disabled={disabled}
              data-testid={`rte-color-${c.value.slice(1)}`}
              // Обводка, а не заливка кнопки: заливка совпала бы с самим цветом
              // и «выбран» стало бы неотличимо от «просто такой цвет».
              className={`h-5 w-5 shrink-0 rounded-full border transition-transform disabled:opacity-40 ${
                currentColor.toLowerCase() === c.value
                  ? 'scale-110 border-[var(--color-text-strong)] ring-1 ring-[var(--color-text-strong)]'
                  : 'border-black/15 hover:scale-110'
              }`}
              style={{ backgroundColor: c.value }}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().setColor(c.value).run();
              }}
            />
          ))}
        </span>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
